import {
  IonButton,
  IonContent,
  IonPage,
  IonImg,
  IonIcon,
  IonMenuToggle,
  useIonToast,
} from '@ionic/react';
import { ellipsisVertical } from 'ionicons/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { firstValueFrom } from 'rxjs';
import { IState } from 'appRedux/createStore';
import {
  CasinoService,
  IPlayerEngagementRow,
  IRequestPlayerEngagementReport,
} from 'services/casino';
import { ICasinoSchema } from 'appRedux/models/casinoModels';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  buildPePdfFilename,
  buildPlayerEngagementPdf,
  downloadBlob,
} from './pePdf';
import {
  downloadPlayerEngagementExcel,
  downloadPlayerEngagementExcelSummary,
  priorWindow,
} from './peExcel';
import { downloadPlayerEngagementChartPngZip, peYMax } from './peChartPng';

const AGE_BANDS = [
  'Under 18',
  'Unknown',
  '18-24',
  '25-34',
  '35-44',
  '45-54',
  '55-64',
  '65+',
] as const;
const GENDERS = ['Unknown', 'Male', 'Female'] as const;
const PIE_COLORS = ['#5B8FF9', '#F6BD16', '#F6903D', '#61DDAA', '#7262fd', '#78D3F8', '#9661BC', '#008685'];

function defaultMonthRange(): { fromMonth: string; toMonth: string } {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 1;
  const fmt = (year: number, month: number) =>
    `${year}-${String(month).padStart(2, '0')}`;
  return { fromMonth: fmt(y, 1), toMonth: fmt(y, m) };
}

function monthOptions(spanYears = 3): string[] {
  const now = new Date();
  const out: string[] = [];
  for (let i = 0; i <= spanYears * 12; i += 1) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

function monthsBetween(fromMonth: string, toMonth: string): string[] {
  const [fy, fm] = fromMonth.split('-').map(Number);
  const [ty, tm] = toMonth.split('-').map(Number);
  const out: string[] = [];
  let y = fy;
  let m = fm;
  while (y < ty || (y === ty && m <= tm)) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

type PeMixMode = 'age' | 'gender' | 'genderByAge';

function buildPeMixData(
  rows: IPlayerEngagementRow[],
  month: string,
  mode: PeMixMode,
  ageBands: string[],
  genders: string[]
): Array<{ name: string; value: number }> {
  const inMonth = rows.filter((r) => r.month === month);
  if (mode === 'age') {
    return ageBands
      .map((band) => ({
        name: band,
        value: inMonth
          .filter((r) => r.ageBand === band)
          .reduce((s, r) => s + (r.activeUniqueLinkedAccounts || 0), 0),
      }))
      .filter((d) => d.value > 0);
  }
  if (mode === 'gender') {
    return genders
      .map((g) => ({
        name: g,
        value: inMonth
          .filter((r) => r.gender === g)
          .reduce((s, r) => s + (r.activeUniqueLinkedAccounts || 0), 0),
      }))
      .filter((d) => d.value > 0);
  }
  // Gender counts by Age Range — age × gender slices
  const out: Array<{ name: string; value: number }> = [];
  for (const band of ageBands) {
    for (const g of genders) {
      const value = inMonth
        .filter((r) => r.ageBand === band && r.gender === g)
        .reduce((s, r) => s + (r.activeUniqueLinkedAccounts || 0), 0);
      if (value > 0) out.push({ name: `${band} · ${g}`, value });
    }
  }
  return out;
}

function colorForMixSlice(name: string, index: number): string {
  const bandIdx = (AGE_BANDS as readonly string[]).indexOf(name);
  if (bandIdx >= 0) return PIE_COLORS[bandIdx % PIE_COLORS.length];
  const genderIdx = (GENDERS as readonly string[]).indexOf(name);
  if (genderIdx >= 0) return PIE_COLORS[genderIdx % PIE_COLORS.length];
  // age · gender compound
  const bandPart = name.split(' · ')[0];
  const bi = (AGE_BANDS as readonly string[]).indexOf(bandPart);
  if (bi >= 0) return PIE_COLORS[(bi + index) % PIE_COLORS.length];
  return PIE_COLORS[index % PIE_COLORS.length];
}



type MultiDropdownProps = {
  label: string;
  options: readonly string[];
  selected: string[];
  onChange: (next: string[]) => void;
  allLabel: string;
};

const MultiDropdown = ({ label, options, selected, onChange, allLabel }: MultiDropdownProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const summary =
    selected.length === 0 || selected.length === options.length
      ? allLabel
      : selected.length === 1
        ? selected[0]
        : `${selected.length} selected`;

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="player-engagement__field" ref={ref}>
      <span className="player-engagement__field-label">{label}</span>
      <div className="player-engagement__multi">
        <button
          type="button"
          className="player-engagement__multi-trigger"
          onClick={() => setOpen((v) => !v)}
        >
          {summary}
        </button>
        {open && (
          <div className="player-engagement__multi-panel">
            <label className="player-engagement__multi-option">
              <input
                type="checkbox"
                checked={selected.length === 0 || selected.length === options.length}
                onChange={() => onChange([])}
              />
              {allLabel}
            </label>
            {options.map((opt) => (
              <label key={opt} className="player-engagement__multi-option">
                <input
                  type="checkbox"
                  checked={selected.length === 0 ? false : selected.includes(opt)}
                  onChange={() => {
                    if (selected.length === 0) {
                      onChange([opt]);
                    } else {
                      toggle(opt);
                    }
                  }}
                />
                {opt}
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


/** Scalar from Recharts ValueType (string | number | array). */
const peTooltipScalar = (v: unknown): string | number | undefined => {
  if (v == null || v === '') return undefined;
  if (Array.isArray(v)) return v.length ? peTooltipScalar(v[0]) : undefined;
  if (typeof v === 'string' || typeof v === 'number') return v;
  return undefined;
};

type PeTooltipProps = {
  active?: boolean;
  payload?: Array<{
    // Align with recharts Payload<ValueType, NameType>
    name?: string | number;
    value?: string | number | Array<string | number>;
    color?: string;
    fill?: string;
    dataKey?: string | number;
    payload?: Record<string, unknown>;
  }>;
  label?: string | number;
  metricLabel: string;
  /** Override header month (Pie has no categorical label). */
  monthLabel?: string;
  /** Format: int (default) or avg (2 decimals). */
  valueFormat?: 'int' | 'avg';
  /** Mix pie denom. Stacked count hovers use payload total when omitted. */
  mixTotal?: number;
};

/** Dark chart tooltip — Figma frame 16 Chart tooltip SoT. */
const PeChartTooltip = ({
  active,
  payload,
  label,
  metricLabel,
  monthLabel,
  valueFormat = 'int',
  mixTotal,
}: PeTooltipProps) => {
  if (!active || !payload?.length) return null;
  const rows = payload
    .map((entry) => ({
      ...entry,
      value: peTooltipScalar(entry?.value),
      name: entry?.name,
    }))
    .filter((entry) => entry != null && entry.value != null && entry.value !== '');
  if (!rows.length) return null;

  const month = String(monthLabel ?? label ?? '');
  const fmt = (v: number | string) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    if (valueFormat === 'avg') return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return Math.round(n).toLocaleString();
  };
  const total = rows.reduce((sum, r) => sum + (Number(r.value) || 0), 0);
  const countDenom =
    typeof mixTotal === 'number' && mixTotal >= 0 ? mixTotal : total;
  const showCountPct = valueFormat !== 'avg';
  const fmtCountPct = (v: number | string) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    const count = Math.round(n).toLocaleString();
    const pct = countDenom > 0 ? (n / countDenom) * 100 : 0;
    return `${count} · ${pct.toFixed(1)}%`;
  };

  return (
    <div className="player-engagement__tooltip">
      <div className="player-engagement__tooltip-header">
        {month ? `${month} UTC · ${metricLabel}` : metricLabel}
      </div>
      <div className="player-engagement__tooltip-rows">
        {rows.map((entry) => {
          const swatch = entry.color || entry.fill || '#5B8FF9';
          const key = String(entry.dataKey ?? entry.name);
          return (
            <div key={key} className="player-engagement__tooltip-row">
              <span
                className="player-engagement__tooltip-swatch"
                style={{ backgroundColor: swatch }}
              />
              <span className="player-engagement__tooltip-label">
                {entry.name != null ? String(entry.name) : key}
              </span>
              <span className="player-engagement__tooltip-value">
                {showCountPct ? fmtCountPct(entry.value as number | string) : fmt(entry.value as number | string)}
              </span>
            </div>
          );
        })}
      </div>
      {rows.length > 1 && (
        <div className="player-engagement__tooltip-total">
          <span>Total</span>
          <span>{showCountPct ? fmtCountPct(total) : fmt(total)}</span>
        </div>
      )}
    </div>
  );
};

const peTooltipSharedProps = {
  // Reposition/clamp inside the chart viewBox (no overflow:hidden clip).
  // Offset upward so the panel prefers above the cursor / away from bottom legend.
  wrapperStyle: { outline: 'none', zIndex: 30, pointerEvents: 'none' as const },
  allowEscapeViewBox: { x: false, y: false },
  offset: 18,
  cursor: { fill: 'rgba(255,255,255,0.06)' },
  isAnimationActive: false,
};

/** Shared plot margin: axis band (~44) + gap before 2-row legend (PE_CHART_POLISH). */
const peChartMargin = { top: 8, right: 12, left: 4, bottom: 44 };


const PeMenuKebab = () => (
  <div className="player-engagement__menu-fab">
    <IonMenuToggle menu="main">
      <IonButton
        fill="clear"
        className="player-engagement__menu-btn"
        aria-label="Open menu"
      >
        <IonIcon icon={ellipsisVertical} className="player-engagement__menu-icon" />
      </IonButton>
    </IonMenuToggle>
  </div>
);

const PeMenuBack = () => (
  <IonMenuToggle menu="main">
    <IonButton
      fill="clear"
      className="player-engagement__back"
      aria-label="Open menu"
    >
      <IonImg src="assets/images/back-icon.svg" />
    </IonButton>
  </IonMenuToggle>
);

const ForbiddenCard = () => (
  <IonPage className="app-parent player-engagement">
    <IonContent className="player-engagement">
      <PeMenuKebab />
      <div className="player-engagement__content">
        <div className="player-engagement__breadcrumb-row">
          <PeMenuBack />
          <div className="player-engagement__breadcrumb">
            Reports / <span>Player Engagement</span>
          </div>
        </div>
        <div className="player-engagement__forbidden-card">
          <h2>403 · Reporting not enabled</h2>
          <p>
            Player Engagement reporting requires the Cognito group{' '}
            <strong>slotcheck-enterprise-reporting</strong>. Ask ops to assign it for your account.
          </p>
        </div>
      </div>
    </IonContent>
  </IonPage>
);

export const PlayerEngagementReportPage = () => {
  const casinoSvc = useMemo(() => new CasinoService(), []);
  const [presentToast] = useIonToast();
  const groups =
    (useSelector<IState>((s) => s.app.persistedState.userInfo?.groups) as string[]) || [];
  const defaults = useMemo(() => defaultMonthRange(), []);
  const [fromMonth, setFromMonth] = useState(defaults.fromMonth);
  const [toMonth, setToMonth] = useState(defaults.toMonth);
  const [ageFilter, setAgeFilter] = useState<string[]>([]);
  const [genderFilter, setGenderFilter] = useState<string[]>([]);
  const [casinoFilter, setCasinoFilter] = useState('');
  const [rows, setRows] = useState<IPlayerEngagementRow[]>([]);
  const [empty, setEmpty] = useState(false);
  const [loading, setLoading] = useState(false);
  /** idle = no casino; loading = fetch in flight; ready = paint G6/empty/charts */
  const [reportPhase, setReportPhase] = useState<'idle' | 'loading' | 'ready'>('idle');
  const loadSeq = useRef(0);
  const presentToastRef = useRef(presentToast);
  presentToastRef.current = presentToast;
  const [selectedMonth, setSelectedMonth] = useState<string>(defaults.toMonth);
  const [mixMode, setMixMode] = useState<'age' | 'gender' | 'genderByAge'>('age');
  const [openCharts, setOpenCharts] = useState<Record<string, boolean>>({
    g2: false,
    g3: false,
    g4: false,
  });
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFlipUp, setExportFlipUp] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const [casinoOptions, setCasinoOptions] = useState<ICasinoSchema[]>([]);

  const canAccess = groups.includes('slotcheck-enterprise-reporting');

  useEffect(() => {
    if (!exportOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!exportMenuRef.current?.contains(e.target as Node)) setExportOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExportOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [exportOpen]);

  useEffect(() => {
    if (!exportOpen) {
      setExportFlipUp(false);
      return;
    }
    const menu = exportMenuRef.current;
    if (!menu) return;
    const btn = menu.querySelector('.player-engagement__export') as HTMLElement | null;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const panelH = 5 * 56 + 24; // ~5 rows + padding
    const spaceBelow = window.innerHeight - rect.bottom;
    const isPhone = window.matchMedia('(max-width: 767px)').matches;
    setExportFlipUp(!isPhone && spaceBelow < panelH + 12);
  }, [exportOpen]);

  // Empty selection = All. Otherwise the explicit subset (even if length === all).
  const activeAgeBands = useMemo(
    () => (ageFilter.length > 0 ? ageFilter : ([...AGE_BANDS] as string[])),
    [ageFilter]
  );
  const activeGenders = useMemo(
    () => (genderFilter.length > 0 ? genderFilter : ([...GENDERS] as string[])),
    [genderFilter]
  );

  // Force G1–G4 legend keys to the selected age bands only (Recharts can otherwise
  // keep stale/full payloads). Same set as the Bar/Line series.
  const ageBandLegendPayload = useMemo(
    () =>
      activeAgeBands.map((b) => ({
        value: b,
        type: 'square' as const,
        id: b,
        color: PIE_COLORS[(AGE_BANDS as readonly string[]).indexOf(b) % PIE_COLORS.length],
      })),
    [activeAgeBands]
  );

  const buildParams = useCallback((): IRequestPlayerEngagementReport => {
    // v1 live QA: single selected casino only (no All / no breakout).
    const params: IRequestPlayerEngagementReport = {
      fromMonth,
      toMonth,
      breakout: 'none',
      includePartialMonth: 'true',
      casinoIds: casinoFilter,
    };
    // Always send when user has narrowed (or explicitly selected a full set).
    // Omit only for true All (empty selection) so API defaults to full grid.
    if (ageFilter.length > 0) {
      params.ageBands = ageFilter.join(',');
    }
    if (genderFilter.length > 0) {
      params.genders = genderFilter.join(',');
    }
    return params;
  }, [fromMonth, toMonth, ageFilter, genderFilter, casinoFilter]);

  const load = useCallback(async () => {
    if (!canAccess || !casinoFilter) {
      loadSeq.current += 1;
      setLoading(false);
      setReportPhase('idle');
      setRows([]);
      setEmpty(false);
      return;
    }
    const seq = ++loadSeq.current;
    setLoading(true);
    setReportPhase('loading');
    try {
      const res = await firstValueFrom(casinoSvc.GetPlayerEngagementReport(buildParams()));
      if (seq !== loadSeq.current) return;
      // 304 / cached empty body can yield '' or undefined — treat as empty dataset.
      const payload = res && typeof res === 'object' ? (res as any) : null;
      const ds = payload?.DataSet;
      const data: IPlayerEngagementRow[] = Array.isArray(ds) ? ds : [];
      // meta.empty drives empty-card vs charts. G6 always sums DataSet rows as returned.
      const isEmpty = Boolean(payload?.meta?.empty) || data.length === 0;
      setRows(data);
      setEmpty(isEmpty);
      setReportPhase('ready');
      if (data.length) {
        const latest = [...data].sort((a, b) => ((a.month || '') < (b.month || '') ? 1 : -1))[0]
          ?.month;
        if (latest) setSelectedMonth(latest);
      }
    } catch (err: any) {
      if (seq !== loadSeq.current) return;
      const status = err?.response?.status || err?.status;
      // Always leave idle and paint empty chrome after a selected-casino attempt.
      setRows([]);
      setEmpty(true);
      setReportPhase('ready');
      if (status === 403 || status === 304) {
        // empty chrome only
      } else {
        presentToastRef.current({
          position: 'top',
          duration: 4000,
          color: 'danger',
          message: 'Failed to load Player Engagement report.',
        }).catch(() => null);
      }
    } finally {
      if (seq === loadSeq.current) {
        setLoading(false);
      }
    }
  }, [buildParams, canAccess, casinoFilter, casinoSvc]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!canAccess) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await firstValueFrom(casinoSvc.GetAllCasinos());
        const list = (res?.DataSet || []).filter((c) => {
          if (!c.isactive || c.isComingSoon) return false;
          // Exclude internal "Slot Check" casino (typically casinoId / kpCasinoPk 4)
          const id = Number(c.kpCasinoPk ?? c.casinoId);
          if (id === 4) return false;
          const name = `${c.shortName || ''} ${c.displayName || ''} ${c.casinoName || ''}`.toLowerCase();
          if (name.includes('slot check') || name.includes('slotcheck')) return false;
          return true;
        });
        list.sort((a, b) =>
          (a.shortName || a.displayName || a.casinoName || '').localeCompare(
            b.shortName || b.displayName || b.casinoName || ''
          )
        );
        if (!cancelled) setCasinoOptions(list);
      } catch {
        if (!cancelled) setCasinoOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canAccess, casinoSvc]);

  // Client-side re-scope so G1–G6 always match the dropdowns even if the API
  // returns a wider zero-fill grid (or ignores ageBands/genders).
  const displayRows = useMemo(() => {
    const ageSet = new Set(activeAgeBands);
    const genderSet = new Set(activeGenders);
    return rows.filter(
      (r) => ageSet.has(String(r.ageBand || '')) && genderSet.has(String(r.gender || ''))
    );
  }, [rows, activeAgeBands, activeGenders]);

  const monthSeries = useMemo(() => {
    const months = monthsBetween(fromMonth, toMonth);
    return months.map((month) => {
      const inMonth = displayRows.filter((r) => r.month === month);
      const uniques = inMonth.reduce((s, r) => s + (r.activeUniqueLinkedAccounts || 0), 0);
      const sessions = inMonth.reduce((s, r) => s + (r.activeSessions || 0), 0);
      const created = inMonth.reduce((s, r) => s + (r.accountsCreated || 0), 0);
      const point: Record<string, number | string> = {
        month,
        uniques,
        sessions,
        created,
        avg: uniques === 0 ? 0 : sessions / uniques,
      };
      for (const band of activeAgeBands) {
        const bandRows = inMonth.filter((r) => r.ageBand === band);
        point[`uniques_${band}`] = bandRows.reduce(
          (s, r) => s + (r.activeUniqueLinkedAccounts || 0),
          0
        );
        point[`sessions_${band}`] = bandRows.reduce((s, r) => s + (r.activeSessions || 0), 0);
        point[`created_${band}`] = bandRows.reduce((s, r) => s + (r.accountsCreated || 0), 0);
        const u = Number(point[`uniques_${band}`]);
        const sess = Number(point[`sessions_${band}`]);
        point[`avg_${band}`] = u === 0 ? 0 : sess / u;
      }
      return point;
    });
  }, [displayRows, fromMonth, toMonth, activeAgeBands]);

  /** yMax = niceMax(peak * 1.10) for G1/G2/G4 stacked totals + G3 line (SoT). */
  const chartYMax = useMemo(() => {
    let uPeak = 0;
    let sPeak = 0;
    let cPeak = 0;
    let aPeak = 0;
    monthSeries.forEach((m) => {
      let u = 0;
      let s = 0;
      let c = 0;
      activeAgeBands.forEach((b) => {
        u += Number(m[`uniques_${b}`] || 0);
        s += Number(m[`sessions_${b}`] || 0);
        c += Number(m[`created_${b}`] || 0);
        aPeak = Math.max(aPeak, Number(m[`avg_${b}`] || 0));
      });
      uPeak = Math.max(uPeak, u);
      sPeak = Math.max(sPeak, s);
      cPeak = Math.max(cPeak, c);
    });
    return {
      g1: peYMax(uPeak),
      g2: peYMax(sPeak),
      g3: peYMax(aPeak),
      g4: peYMax(cPeak),
    };
  }, [monthSeries, activeAgeBands]);

  const kpiUniques = useMemo(
    () => monthSeries.reduce((s, m) => s + Number(m.uniques || 0), 0),
    [monthSeries]
  );
  const kpiCreated = useMemo(
    () => monthSeries.reduce((s, m) => s + Number(m.created || 0), 0),
    [monthSeries]
  );
  const kpiSessions = useMemo(
    () => monthSeries.reduce((s, m) => s + Number(m.sessions || 0), 0),
    [monthSeries]
  );
  const kpiAvgLogins = useMemo(
    () => (kpiUniques === 0 ? 0 : kpiSessions / kpiUniques),
    [kpiUniques, kpiSessions]
  );

  /** MoM delta from last two months in range (frame 19 — delta under KPI). */
  const kpiMom = useMemo(() => {
    const fmt = (key: 'uniques' | 'created') => {
      if (monthSeries.length < 2) {
        return { text: 'Selected range total', cls: 'is-flat' as const };
      }
      const last = Number(monthSeries[monthSeries.length - 1][key] || 0);
      const prev = Number(monthSeries[monthSeries.length - 2][key] || 0);
      const lastLabel = String(monthSeries[monthSeries.length - 1].month || '');
      if (prev === 0) {
        if (last === 0) return { text: `${lastLabel} · flat`, cls: 'is-flat' as const };
        return { text: `${lastLabel} · new`, cls: 'is-up' as const };
      }
      const pct = ((last - prev) / prev) * 100;
      const rounded = Math.round(pct);
      if (rounded === 0) return { text: `${lastLabel} · 0% MoM`, cls: 'is-flat' as const };
      const sign = rounded > 0 ? '+' : '';
      return {
        text: `${lastLabel} · ${sign}${rounded}% MoM`,
        cls: (rounded > 0 ? 'is-up' : 'is-down') as 'is-up' | 'is-down',
      };
    };
    return { uniques: fmt('uniques'), created: fmt('created') };
  }, [monthSeries]);

  const showPartialBadge = useMemo(
    () => displayRows.some((r) => r.month === toMonth && r.isPartial),
    [displayRows, toMonth]
  );

  const mixData = useMemo(
    () =>
      buildPeMixData(displayRows, selectedMonth, mixMode, activeAgeBands, activeGenders),
    [displayRows, selectedMonth, mixMode, activeAgeBands, activeGenders]
  );

  const mixTotal = useMemo(
    () => mixData.reduce<number>((s, d) => s + d.value, 0),
    [mixData]
  );

  const tableRows = useMemo(
    () =>
      [...displayRows].sort((a, b) => {
        if (a.month !== b.month) return (a.month || '') < (b.month || '') ? 1 : -1;
        if (a.ageBand !== b.ageBand) return (a.ageBand || '').localeCompare(b.ageBand || '');
        return (a.gender || '').localeCompare(b.gender || '');
      }),
    [displayRows]
  );

  const exportCsv = async () => {
    try {
      const blob = await firstValueFrom(casinoSvc.GetPlayerEngagementReportExport(buildParams()));
      const url = URL.createObjectURL(blob as unknown as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `player-engagement-${fromMonth}_${toMonth}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      presentToast({
        position: 'top',
        duration: 4000,
        color: 'danger',
        message: 'CSV export failed.',
      }).catch(() => null);
    }
  };

  const exportPdf = () => {
    if (!casinoFilter || reportPhase !== 'ready') {
      presentToast({
        position: 'top',
        duration: 3000,
        color: 'warning',
        message: 'Select a casino and wait for the report before exporting PDF.',
      }).catch(() => null);
      return;
    }
    try {
      const casinoMeta = casinoOptions.find((c) => String(c.kpCasinoPk ?? c.casinoId) === String(casinoFilter));
      const casinoLabel =
        casinoMeta?.shortName || casinoMeta?.displayName || casinoMeta?.casinoName || String(casinoFilter);
      const ageLabel = ageFilter.length ? ageFilter.join(', ') : 'All';
      const genderLabel = genderFilter.length ? genderFilter.join(', ') : 'All';
      const g5Month =
        monthSeries.length > 0
          ? String(monthSeries[monthSeries.length - 1].month)
          : toMonth;
      const g5ModeLabel =
        mixMode === 'age'
          ? 'Age'
          : mixMode === 'gender'
            ? 'Gender'
            : 'Gender counts by Age Range';
      const g5Views = [
        {
          label: g5ModeLabel,
          slices: buildPeMixData(
            displayRows,
            g5Month,
            mixMode,
            activeAgeBands,
            activeGenders
          ).map((d, i) => ({
            ...d,
            color: colorForMixSlice(d.name, i),
          })),
        },
      ];
      const blob = buildPlayerEngagementPdf({
        casinoLabel,
        fromMonth,
        toMonth,
        ageLabel,
        genderLabel,
        generatedAt: new Date(),
        kpis: {
          uniqueActives: kpiUniques,
          accountsCreated: kpiCreated,
          sessions: kpiSessions,
          avgLogins: kpiAvgLogins,
        },
        ageBands: activeAgeBands,
        bandColors: activeAgeBands.map(
          (b) => PIE_COLORS[(AGE_BANDS as readonly string[]).indexOf(b) % PIE_COLORS.length]
        ),
        monthSeries: monthSeries.map((m) => {
          const byBand: Record<
            string,
            { uniques: number; sessions: number; created: number; avg: number }
          > = {};
          for (const b of activeAgeBands) {
            byBand[b] = {
              uniques: Number(m[`uniques_${b}`] || 0),
              sessions: Number(m[`sessions_${b}`] || 0),
              created: Number(m[`created_${b}`] || 0),
              avg: Number(m[`avg_${b}`] || 0),
            };
          }
          return {
            month: String(m.month),
            uniques: Number(m.uniques || 0),
            sessions: Number(m.sessions || 0),
            created: Number(m.created || 0),
            avg: Number(m.avg || 0),
            byBand,
          };
        }),
        g5Month,
        g5Views,
      });
      downloadBlob(blob, buildPePdfFilename(casinoLabel, fromMonth, toMonth));
      setExportOpen(false);
    } catch {
      presentToast({
        position: 'top',
        duration: 4000,
        color: 'danger',
        message: 'PDF export failed.',
      }).catch(() => null);
    }
  };

  const casinoLabelForExport = () => {
    const casinoMeta = casinoOptions.find(
      (c) => String(c.kpCasinoPk ?? c.casinoId) === String(casinoFilter)
    );
    return (
      casinoMeta?.shortName ||
      casinoMeta?.displayName ||
      casinoMeta?.casinoName ||
      String(casinoFilter)
    );
  };

  const exportExcel = async (mode: 'full' | 'summary' = 'full') => {
    if (!casinoFilter || reportPhase !== 'ready') {
      presentToast({
        position: 'top',
        duration: 3000,
        color: 'warning',
        message: 'Select a casino and wait for the report before exporting Excel.',
      }).catch(() => null);
      return;
    }
    try {
      const { priorFrom, priorTo } = priorWindow(fromMonth, toMonth);
      const res = await firstValueFrom(
        casinoSvc.GetPlayerEngagementReport({
          ...buildParams(),
          fromMonth: priorFrom,
          toMonth,
        })
      );
      const ds = (res as { DataSet?: IPlayerEngagementRow[] })?.DataSet;
      const allRows: IPlayerEngagementRow[] = Array.isArray(ds) ? ds : [];
      const inBands = (r: IPlayerEngagementRow) =>
        activeAgeBands.includes(r.ageBand) && activeGenders.includes(r.gender);
      const curRows = allRows.filter(
        (r) => r.month >= fromMonth && r.month <= toMonth && inBands(r)
      );
      const priorRows = allRows.filter(
        (r) => r.month >= priorFrom && r.month <= priorTo && inBands(r)
      );
      const sum = (rows: IPlayerEngagementRow[]) => {
        const uniqueLinked = rows.reduce((s, r) => s + (r.activeUniqueLinkedAccounts || 0), 0);
        const sessions = rows.reduce((s, r) => s + (r.activeSessions || 0), 0);
        const accountsCreated = rows.reduce((s, r) => s + (r.accountsCreated || 0), 0);
        return {
          uniqueLinked,
          sessions,
          accountsCreated,
          avgLogins: uniqueLinked === 0 ? 0 : sessions / uniqueLinked,
        };
      };
      const payload = {
        casinoLabel: casinoLabelForExport(),
        fromMonth,
        toMonth,
        ageLabel: ageFilter.length ? ageFilter.join(', ') : 'All',
        genderLabel: genderFilter.length ? genderFilter.join(', ') : 'All',
        generatedAt: new Date(),
        kpis: sum(curRows),
        priorKpis: sum(priorRows),
        priorFromMonth: priorFrom,
        priorToMonth: priorTo,
        detailRows: displayRows.map((r) => ({
          month: r.month,
          ageBand: r.ageBand,
          gender: r.gender,
          activeUniqueLinkedAccounts: r.activeUniqueLinkedAccounts || 0,
          activeSessions: r.activeSessions || 0,
          accountsCreated: r.accountsCreated || 0,
          isPartial: Boolean(r.isPartial),
        })),
      };
      if (mode === 'summary') {
        await downloadPlayerEngagementExcelSummary(payload);
      } else {
        await downloadPlayerEngagementExcel(payload);
      }
      setExportOpen(false);
    } catch {
      presentToast({
        position: 'top',
        duration: 4000,
        color: 'danger',
        message: 'Excel export failed.',
      }).catch(() => null);
    }
  };

  const exportChartPngs = async () => {
    if (!casinoFilter || reportPhase !== 'ready') {
      presentToast({
        position: 'top',
        duration: 3000,
        color: 'warning',
        message: 'Select a casino and wait for the report before exporting chart PNGs.',
      }).catch(() => null);
      return;
    }
    try {
      const g5MonthLocal =
        monthSeries.length > 0
          ? String(monthSeries[monthSeries.length - 1].month)
          : toMonth;
      const g5Key =
        mixMode === 'age' ? 'Age' : mixMode === 'gender' ? 'Gender' : 'GenderByAge';
      const g5Title =
        mixMode === 'age'
          ? 'G5 · Linked Accounts · Age'
          : mixMode === 'gender'
            ? 'G5 · Linked Accounts · Gender'
            : 'G5 · Linked Accounts · Gender counts by Age Range';
      const g5Views = [
        {
          key: g5Key,
          title: g5Title,
          mix: buildPeMixData(
            displayRows,
            g5MonthLocal,
            mixMode,
            activeAgeBands,
            activeGenders
          ).map((d, i) => ({
            ...d,
            color: colorForMixSlice(d.name, i),
          })),
        },
      ];
      await downloadPlayerEngagementChartPngZip({
        casinoLabel: casinoLabelForExport(),
        fromMonth,
        toMonth,
        ageBands: activeAgeBands,
        bandColors: activeAgeBands.map(
          (b) => PIE_COLORS[(AGE_BANDS as readonly string[]).indexOf(b) % PIE_COLORS.length]
        ),
        monthSeries: monthSeries.map((m) => {
          const byBand: Record<
            string,
            { uniques: number; sessions: number; created: number; avg: number }
          > = {};
          for (const b of activeAgeBands) {
            byBand[b] = {
              uniques: Number(m[`uniques_${b}`] || 0),
              sessions: Number(m[`sessions_${b}`] || 0),
              created: Number(m[`created_${b}`] || 0),
              avg: Number(m[`avg_${b}`] || 0),
            };
          }
          return {
            month: String(m.month),
            uniques: Number(m.uniques || 0),
            sessions: Number(m.sessions || 0),
            created: Number(m.created || 0),
            avg: Number(m.avg || 0),
            byBand,
          };
        }),
        g5Month: g5MonthLocal,
        g5Views,
      });
      setExportOpen(false);
    } catch {
      presentToast({
        position: 'top',
        duration: 4000,
        color: 'danger',
        message: 'Chart PNG export failed.',
      }).catch(() => null);
    }
  };


  const resetFilters = () => {
    const next = defaultMonthRange();
    setFromMonth(next.fromMonth);
    setToMonth(next.toMonth);
    setAgeFilter([]);
    setGenderFilter([]);
    setCasinoFilter('');
    setReportPhase('idle');
    setRows([]);
    setEmpty(false);
    setLoading(false);
  };

  if (!canAccess) {
    return <ForbiddenCard />;
  }

  const toggleChart = (key: string) =>
    setOpenCharts((prev) => ({ ...prev, [key]: !prev[key] }));

  const months = monthOptions();

  return (
    <IonPage className="app-parent player-engagement">
      <IonContent className="player-engagement">
        <PeMenuKebab />
        <div className="player-engagement__content">
          <div className="player-engagement__breadcrumb-row">
            <PeMenuBack />
            <div className="player-engagement__breadcrumb">
              Reports / <span>Player Engagement</span>
            </div>
          </div>

          <div className="player-engagement__header">
            <div className="player-engagement__title-row">
              <h1 className="player-engagement__title">Player Engagement</h1>
            </div>
            <div className="player-engagement__subtitle-row">
              <p className="player-engagement__subtitle">
                Active Linked Accounts, sessions, avg logins, New Linked Accounts — Month × Age Band ×
                Gender.
              </p>
              {showPartialBadge && (
                <span className="player-engagement__badge">MTD (UTC) · partial month</span>
              )}
            </div>
          </div>

          <div className="player-engagement__filters">
            <div className="player-engagement__field">
              <span className="player-engagement__field-label">Casino</span>
              <select
                className="player-engagement__select"
                value={casinoFilter}
                onChange={(e) => setCasinoFilter(e.target.value)}
              >
                <option value="">Select a casino…</option>
                {casinoOptions.map((c) => {
                  const id = c.kpCasinoPk ?? c.casinoId;
                  const label = c.shortName || c.displayName || c.casinoName || String(id);
                  return (
                    <option key={id} value={String(id)}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="player-engagement__field player-engagement__field--month">
              <span className="player-engagement__field-label">Month range (UTC)</span>
              <div className="player-engagement__month-range">
                <select
                  className="player-engagement__select"
                  value={fromMonth}
                  onChange={(e) => setFromMonth(e.target.value)}
                >
                  {months.map((m) => (
                    <option key={`from-${m}`} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <span className="player-engagement__month-sep">–</span>
                <select
                  className="player-engagement__select"
                  value={toMonth}
                  onChange={(e) => setToMonth(e.target.value)}
                >
                  {months.map((m) => (
                    <option key={`to-${m}`} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <span className="player-engagement__field-hint">Months are UTC calendar labels.</span>
            </div>

            <MultiDropdown
              label="Age bands"
              options={AGE_BANDS}
              selected={ageFilter}
              onChange={setAgeFilter}
              allLabel="All (multi-select)"
            />
            <MultiDropdown
              label="Gender"
              options={GENDERS}
              selected={genderFilter}
              onChange={setGenderFilter}
              allLabel="All (multi-select)"
            />

            <div className="player-engagement__export-field">
              <span className="player-engagement__field-label" aria-hidden="true">
                &nbsp;
              </span>
              <div className="player-engagement__export-menu" ref={exportMenuRef}>
                <IonButton
                  className="player-engagement__export"
                  disabled={!casinoFilter}
                  onClick={() => setExportOpen((o) => !o)}
                >
                  Export ▾
                </IonButton>
                {exportOpen && (
                  <>
                    <button
                      type="button"
                      className="player-engagement__export-backdrop"
                      aria-label="Close export menu"
                      onClick={() => setExportOpen(false)}
                    />
                    <div
                      className={`player-engagement__export-dropdown${exportFlipUp ? ' is-up' : ''}`}
                      role="menu"
                    >
                      <div className="player-engagement__export-sheet-chrome" aria-hidden="true">
                        <span className="player-engagement__export-sheet-handle" />
                        <div className="player-engagement__export-sheet-title">Export</div>
                      </div>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={!casinoFilter}
                        onClick={() => {
                          setExportOpen(false);
                          void exportCsv();
                        }}
                      >
                        <span className="player-engagement__export-item-title">CSV</span>
                        <span className="player-engagement__export-item-sub">
                          Raw table download
                        </span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={!casinoFilter || reportPhase !== 'ready'}
                        onClick={() => {
                          setExportOpen(false);
                          exportPdf();
                        }}
                      >
                        <span className="player-engagement__export-item-title">PDF snapshot</span>
                        <span className="player-engagement__export-item-sub">
                          GM / ops pack
                        </span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={!casinoFilter || reportPhase !== 'ready'}
                        onClick={() => void exportExcel('full')}
                      >
                        <span className="player-engagement__export-item-title">
                          Excel Summary and Detail Data
                        </span>
                        <span className="player-engagement__export-item-sub">
                          Full workbook
                        </span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={!casinoFilter || reportPhase !== 'ready'}
                        onClick={() => void exportExcel('summary')}
                      >
                        <span className="player-engagement__export-item-title">Excel Summary</span>
                        <span className="player-engagement__export-item-sub">Summary sheet only</span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={!casinoFilter || reportPhase !== 'ready'}
                        onClick={() => void exportChartPngs()}
                      >
                        <span className="player-engagement__export-item-title">Chart PNGs (zip)</span>
                        <span className="player-engagement__export-item-sub">
                          G1–G5 images
                        </span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {reportPhase === 'idle' && (
            <div className="player-engagement__card player-engagement__empty">
              <h3>Select a casino to load the report</h3>
              <p>Choose one casino above. The report loads only after you pick a casino.</p>
            </div>
          )}

          {reportPhase === 'loading' && (
            <>
              <div className="player-engagement__kpi-row">
                <div className="player-engagement__kpi">
                  <div className="player-engagement__skeleton" />
                  <div className="player-engagement__skeleton" />
                </div>
                <div className="player-engagement__kpi">
                  <div className="player-engagement__skeleton" />
                  <div className="player-engagement__skeleton" />
                </div>
              </div>
              <div className="player-engagement__charts">
                <div className="player-engagement__chart-card">
                  <div className="player-engagement__skeleton player-engagement__skeleton--lg" />
                </div>
                <div className="player-engagement__chart-card">
                  <div className="player-engagement__skeleton player-engagement__skeleton--lg" />
                </div>
                <div className="player-engagement__chart-card">
                  <div className="player-engagement__skeleton player-engagement__skeleton--lg" />
                </div>
                <div className="player-engagement__chart-card">
                  <div className="player-engagement__skeleton player-engagement__skeleton--lg" />
                </div>
              </div>
            </>
          )}

          {reportPhase === 'ready' && (
            <div className="player-engagement__kpi-row">
              <div className="player-engagement__kpi">
                <div className="player-engagement__kpi-label">Active Linked Accounts</div>
                <div className="player-engagement__kpi-value">
                  {kpiUniques.toLocaleString()}
                </div>
                <div className={`player-engagement__kpi-delta ${kpiMom.uniques.cls}`}>
                  {kpiMom.uniques.text}
                </div>
              </div>
              <div className="player-engagement__kpi">
                <div className="player-engagement__kpi-label">New Linked Accounts</div>
                <div className="player-engagement__kpi-value">
                  {kpiCreated.toLocaleString()}
                </div>
                <div className={`player-engagement__kpi-delta ${kpiMom.created.cls}`}>
                  {kpiMom.created.text}
                </div>
              </div>
            </div>
          )}

          {reportPhase === 'ready' && empty && (
            <div className="player-engagement__card player-engagement__empty">
              <h3>No engagement data for this range</h3>
              <p>Try expanding the month range, clearing age-band filters, or picking another casino.</p>
              <IonButton className="player-engagement__reset" onClick={resetFilters}>
                Reset filters
              </IonButton>
            </div>
          )}

          {reportPhase === 'ready' && !empty && (
            <>
              <div className="player-engagement__charts">
                <div className="player-engagement__chart-card">
                  <div className="player-engagement__chart-head">
                    <div className="player-engagement__chart-head-text">
                      <div className="player-engagement__chart-title">Active Linked Accounts over time</div>
                      <div className="player-engagement__chart-sub">G1 · Monthly · stacked by age band</div>
                    </div>
                  </div>
                  <div className="player-engagement__chart-body">
                  <div className="player-engagement__chart">
                    <ResponsiveContainer>
                      <BarChart
                        data={monthSeries}
                        margin={peChartMargin}
                        onClick={(st: any) => st?.activeLabel && setSelectedMonth(String(st.activeLabel))}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis
                          dataKey="month"
                          stroke="rgba(255,255,255,0.45)"
                          tick={{ fontSize: 11 }}
                          height={44}
                          interval={0}
                          angle={-30}
                          textAnchor="end"
                        />
                        <YAxis
                          stroke="rgba(255,255,255,0.45)"
                          tick={{ fontSize: 11 }}
                          domain={[0, chartYMax.g1]}
                          allowDataOverflow
                        />
                        <Tooltip
                          {...peTooltipSharedProps}
                          content={(props) => (
                            <PeChartTooltip {...props} metricLabel="Active Linked Accounts" />
                          )}
                        />
                        <Legend
                          payload={ageBandLegendPayload}
                          verticalAlign="bottom"
                          align="center"
                          iconSize={10}
                          wrapperStyle={{ paddingTop: 16 }}
                        />
                        {activeAgeBands.map((b) => (
                          <Bar
                            key={b}
                            dataKey={`uniques_${b}`}
                            name={b}
                            stackId="u"
                            fill={PIE_COLORS[(AGE_BANDS as readonly string[]).indexOf(b) % PIE_COLORS.length]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  </div>
                </div>

                {(['g2', 'g3', 'g4'] as const).map((key) => {
                  const title =
                    key === 'g2'
                      ? 'G2 · Sessions over time'
                      : key === 'g3'
                        ? 'G3 · Avg logins per active player'
                        : 'G4 · New Linked Accounts over time';
                  const dataKeyPrefix = key === 'g2' ? 'sessions' : key === 'g3' ? 'avg' : 'created';
                  const sub =
                    key === 'g2'
                      ? 'G2 · Monthly sessions (Sessions stay here — not on G6)'
                      : key === 'g3'
                        ? 'G3 · Avg logins per active player'
                        : 'G4 · New Linked Accounts by month';
                  return (
                    <div key={key} className="player-engagement__chart-card">
                      <div className="player-engagement__chart-head">
                        <div className="player-engagement__chart-head-text">
                          <div className="player-engagement__chart-title">
                            {title.replace(/^G\d · /, '')}
                          </div>
                          <div className="player-engagement__chart-sub">{sub}</div>
                        </div>
                        <IonButton
                          size="small"
                          fill="clear"
                          className="collapse-mobile"
                          onClick={() => toggleChart(key)}
                        >
                          {openCharts[key] ? 'Hide' : 'Show'}
                        </IonButton>
                      </div>
                      <div className="player-engagement__chart-body">
                      <div className={`player-engagement__chart chart-collapsible ${openCharts[key] ? 'is-open' : ''}`}>
                        <ResponsiveContainer>
                          {key === 'g3' ? (
                            <LineChart data={monthSeries} margin={peChartMargin}>
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                              <XAxis
                                dataKey="month"
                                stroke="rgba(255,255,255,0.45)"
                                tick={{ fontSize: 11 }}
                                height={44}
                                interval={0}
                                angle={-30}
                                textAnchor="end"
                              />
                              <YAxis
                                stroke="rgba(255,255,255,0.45)"
                                tick={{ fontSize: 11 }}
                                domain={[0, chartYMax.g3]}
                                allowDataOverflow
                              />
                              <Tooltip
                                {...peTooltipSharedProps}
                                cursor={{ stroke: 'rgba(255,255,255,0.25)' }}
                                content={(props) => (
                                  <PeChartTooltip
                                    {...props}
                                    metricLabel="Avg logins per active player"
                                    valueFormat="avg"
                                  />
                                )}
                              />
                              <Legend
                                payload={ageBandLegendPayload}
                                verticalAlign="bottom"
                                align="center"
                                iconSize={10}
                                wrapperStyle={{ paddingTop: 16 }}
                              />
                              {activeAgeBands.map((b) => (
                                <Line
                                  key={b}
                                  type="monotone"
                                  dataKey={`${dataKeyPrefix}_${b}`}
                                  name={b}
                                  stroke={
                                    PIE_COLORS[(AGE_BANDS as readonly string[]).indexOf(b) % PIE_COLORS.length]
                                  }
                                  dot={false}
                                />
                              ))}
                            </LineChart>
                          ) : (
                            <BarChart data={monthSeries} margin={peChartMargin}>
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                              <XAxis
                                dataKey="month"
                                stroke="rgba(255,255,255,0.45)"
                                tick={{ fontSize: 11 }}
                                height={44}
                                interval={0}
                                angle={-30}
                                textAnchor="end"
                              />
                              <YAxis
                                stroke="rgba(255,255,255,0.45)"
                                tick={{ fontSize: 11 }}
                                domain={[0, key === 'g2' ? chartYMax.g2 : chartYMax.g4]}
                                allowDataOverflow
                              />
                              <Tooltip
                                {...peTooltipSharedProps}
                                content={(props) => (
                                  <PeChartTooltip
                                    {...props}
                                    metricLabel={
                                      key === 'g2' ? 'Sessions' : 'New Linked Accounts'
                                    }
                                  />
                                )}
                              />
                              <Legend
                                payload={ageBandLegendPayload}
                                verticalAlign="bottom"
                                align="center"
                                iconSize={10}
                                wrapperStyle={{ paddingTop: 16 }}
                              />
                              {activeAgeBands.map((b) => (
                                <Bar
                                  key={b}
                                  dataKey={`${dataKeyPrefix}_${b}`}
                                  name={b}
                                  stackId="s"
                                  fill={PIE_COLORS[(AGE_BANDS as readonly string[]).indexOf(b) % PIE_COLORS.length]}
                                />
                              ))}
                            </BarChart>
                          )}
                        </ResponsiveContainer>
                      </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="player-engagement__bottom">
                <div
                  className="player-engagement__chart-card player-engagement__mix-card"
                  style={{ marginBottom: 0 }}
                >
                  <div className="player-engagement__chart-head">
                    <div className="player-engagement__chart-head-text">
                      <div className="player-engagement__chart-title">Linked Accounts</div>
                      <div className="player-engagement__chart-sub">
                        G5 · {selectedMonth} UTC
                      </div>
                    </div>
                    <div
                      className="player-engagement__toggle player-engagement__toggle--g5"
                      role="tablist"
                      aria-label="Linked Accounts view"
                    >
                      <IonButton
                        className={`player-engagement__toggle-btn ${mixMode === 'age' ? 'is-active' : ''}`}
                        onClick={() => setMixMode('age')}
                      >
                        Age
                      </IonButton>
                      <IonButton
                        className={`player-engagement__toggle-btn ${mixMode === 'gender' ? 'is-active' : ''}`}
                        onClick={() => setMixMode('gender')}
                      >
                        Gender
                      </IonButton>
                      <IonButton
                        className={`player-engagement__toggle-btn player-engagement__toggle-btn--wide ${
                          mixMode === 'genderByAge' ? 'is-active' : ''
                        }`}
                        onClick={() => setMixMode('genderByAge')}
                      >
                        Gender counts by Age Range
                      </IonButton>
                    </div>
                  </div>
                  {/* Donut ABOVE, list legend BELOW full width */}
                  <div className="player-engagement__chart-body player-engagement__mix-body">
                    <div className="player-engagement__chart player-engagement__chart--mix">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                          <Pie
                            data={mixData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius="48%"
                            outerRadius="82%"
                            paddingAngle={2}
                          >
                            {mixData.map((d, i) => (
                              <Cell key={d.name} fill={colorForMixSlice(d.name, i)} />
                            ))}
                          </Pie>
                          <Tooltip
                            {...peTooltipSharedProps}
                            content={(props) => (
                              <PeChartTooltip
                                {...props}
                                metricLabel={
                                  mixMode === 'age'
                                    ? 'Linked Accounts · Age'
                                    : mixMode === 'gender'
                                      ? 'Linked Accounts · Gender'
                                      : 'Linked Accounts · Gender counts by Age Range'
                                }
                                monthLabel={selectedMonth}
                                mixTotal={mixTotal}
                              />
                            )}
                          />
                          <text
                            x="50%"
                            y="50%"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill="#fff"
                            fontSize="12"
                          >
                            {mixTotal.toLocaleString()} linked
                          </text>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <ul className="player-engagement__mix-legend" aria-label="Linked Accounts legend">
                      {mixData.map((d, i) => {
                        const pct = mixTotal > 0 ? (d.value / mixTotal) * 100 : 0;
                        return (
                          <li key={d.name} className="player-engagement__mix-legend-item">
                            <span
                              className="player-engagement__mix-legend-swatch"
                              style={{ backgroundColor: colorForMixSlice(d.name, i) }}
                            />
                            <span className="player-engagement__mix-legend-label">{d.name}</span>
                            <span className="player-engagement__mix-legend-meta">
                              {Math.round(d.value).toLocaleString()} · {pct.toFixed(1)}%
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>

                <div
                  className="player-engagement__chart-card player-engagement__table-card"
                  style={{ marginBottom: 0 }}
                >
                  <div className="player-engagement__chart-head">
                    <div className="player-engagement__chart-head-text">
                      <div className="player-engagement__chart-title">Detail table</div>
                      <div className="player-engagement__chart-sub">
                        Month ↓ × Age Band × Gender
                      </div>
                    </div>
                  </div>
                  <div className="player-engagement__table-scroll">
                    <table className="pe-table">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>Age</th>
                          <th>Gender</th>
                          <th>Active Linked Accounts</th>
                          <th>Sessions</th>
                          <th>Avg logins / active</th>
                          <th>New Linked Accounts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableRows.map((r, idx) => (
                          <tr
                            key={`${r.month}-${r.ageBand}-${r.gender}-${idx}`}
                            className={r.month === selectedMonth ? 'is-selected' : ''}
                            onClick={() => setSelectedMonth(r.month)}
                          >
                            <td>{r.month}</td>
                            <td>{r.ageBand}</td>
                            <td>{r.gender}</td>
                            <td className="num">{r.activeUniqueLinkedAccounts}</td>
                            <td className="num">{r.activeSessions}</td>
                            <td className="num">{Number(r.avgLoginsPerActivePlayer).toFixed(2)}</td>
                            <td className="num">{r.accountsCreated}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default PlayerEngagementReportPage;
